/**
 * One folded settings row: the short mono label, the setting as it stands, and
 * the chevron that says the whole row opens its controls in place.
 *
 * Lifted out of the Engagement pane so the repository environment page reads in
 * the same shape. A reader takes in a whole configuration in a handful of
 * lines and opens only the row they came to change.
 *
 * The body stays MOUNTED while it is folded, behind `hidden`. A row is a form:
 * remounting it on every open would throw away a half-typed list, and the
 * controls inside already fetch what they need once.
 */
import type { ReactNode } from "react";
import { ChevronRightIcon } from "../queue/icons.js";

export function SettingsRow({
  section,
  label,
  help,
  summary,
  open,
  onToggle,
  testId,
  children,
}: {
  section: string;
  label: string;
  help?: string;
  summary: ReactNode;
  open: boolean;
  onToggle: () => void;
  testId?: string;
  children: ReactNode;
}) {
  const bodyId = `wrk-setrow-body-${section}`;
  return (
    <section className="wrk-setrow" data-testid={testId} data-open={open ? "true" : undefined}>
      <button
        type="button"
        className="wrk-setrow-head"
        title={help}
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
        data-testid={`wrk-setrow-${section}`}
      >
        <span className="wrk-setrow-lab">{label}</span>
        <span className="wrk-setrow-val">{summary}</span>
        <ChevronRightIcon className="wrk-setrow-go" />
      </button>
      <div id={bodyId} className="wrk-setrow-body" hidden={!open} data-testid={bodyId}>
        {children}
      </div>
    </section>
  );
}
